import { Asset, AssetTypes } from 'state/assets/AssetTypes';
import { FluxStandardAction } from 'state/StateTypes';
import { createReducer } from 'utils';
import * as actionTypes from './AssetActionTypes';

export interface AssetState {
   waveforms: Asset[] | null;
   filteredWaveforms: Asset[] | null;
   sensations: Asset[] | null;
   filteredSensations: Asset[] | null;
   selectedAsset: Asset | null;
}
type AssetType = 'sensations' | 'waveforms' | 'filteredSensations' | 'filteredWaveforms';

const assignPayloadToState = ({ payload, error }: FluxStandardAction, prop: keyof AssetState): Partial<AssetState> => ({
   [prop]: error ? null : payload,
});

const AssetReducer = createReducer<AssetState, FluxStandardAction>(
   {
      waveforms: null,
      filteredWaveforms: null,
      sensations: null,
      filteredSensations: null,
      selectedAsset: null,
   },
   {
      [actionTypes.GET_ASSETS]: (state, { payload }) => ({
         ...state,
         waveforms: payload.waveforms,
         sensations: payload.sensations,
         filteredSensations: null,
         filteredWaveforms: null,
      }),
      [actionTypes.UPDATE_ASSET]: (state, { payload }) => {
         const { _id: updatedAssetId, type } = payload;
         //if the user is filtering assets, we want to make sure to target the correct state property when updating status
         const assetType = determineAssetType({ type, state });
         let unfilteredAssets: 'waveforms' | 'sensations' | undefined;
         if (assetType === 'filteredSensations') {
            unfilteredAssets = 'sensations';
         } else if (assetType === 'filteredWaveforms') {
            unfilteredAssets = 'waveforms';
         }
         //find and replace updated asset in state
         let updatedState: AssetState = {
            ...state,
            [assetType]: state[assetType]?.map((asset: Asset) => (asset._id === updatedAssetId ? payload : asset)),
         };
         if (unfilteredAssets && updatedState[unfilteredAssets]?.find(({ _id }) => _id === updatedAssetId)) {
            updatedState = {
               ...updatedState,
               [unfilteredAssets]: updatedState[unfilteredAssets]?.map(asset => (asset._id === updatedAssetId ? payload : asset)),
            };
         }

         return updatedState || state;
      },
      [actionTypes.DELETE_ASSET]: (state, { payload }) => {
         const { _id: deletedAssetId, type } = payload;
         const assetType = determineAssetType({ type, state });
         let unfilteredAssets: 'waveforms' | 'sensations' | undefined;
         if (assetType === 'filteredSensations') {
            unfilteredAssets = 'sensations';
         } else if (assetType === 'filteredWaveforms') {
            unfilteredAssets = 'waveforms';
         }
         //find and remove deleted asset from state
         let updatedState: AssetState = { ...state, [assetType]: state[assetType]?.filter(({ _id }) => _id !== deletedAssetId) };
         if (unfilteredAssets && updatedState[unfilteredAssets]?.find(({ _id }) => _id === deletedAssetId)) {
            updatedState = {
               ...updatedState,
               [unfilteredAssets]: updatedState[unfilteredAssets]?.filter(({ _id }) => _id !== deletedAssetId),
            };
         }
         return updatedState || state;
      },
      [actionTypes.FILTER_WAVEFORMS]: (state, action) => ({
         ...state,
         ...assignPayloadToState(action, 'filteredWaveforms'),
      }),
      [actionTypes.FILTER_SENSATIONS]: (state, action) => ({
         ...state,
         ...assignPayloadToState(action, 'filteredSensations'),
      }),
      [actionTypes.SET_SELECTED_ASSET]: (state, action) => ({
         ...state,
         ...assignPayloadToState(action, 'selectedAsset'),
      }),
      [actionTypes.CLEAR_SELECTED_ASSET]: state => ({
         ...state,
         selectedAsset: null,
      }),
   }
);

export default AssetReducer;

function determineAssetType({ type, state }: { type: AssetTypes; state: AssetState }): AssetType {
   return type === AssetTypes.Sensation
      ? state.filteredSensations
         ? 'filteredSensations'
         : 'sensations'
      : state.filteredWaveforms
      ? 'filteredWaveforms'
      : 'waveforms';
}
